import React, { useState, useEffect } from 'react';
import { CreditCardIcon, PlusCircleIcon, TrashIcon } from '../icons/HeroIcons';
import axios from 'axios';
import { AISHA_API_BASE } from '@/constants';

interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

interface SavedCardsPaymentProps {
  userId: string;
  totalAmount?: number;
  amount?: number;  // 互換性のため残す
  onSelectCard?: (cardId: string) => void | Promise<void>;
  onAddNewCard?: () => void;
  onPaymentSuccess?: (creditBalance: number) => void;
  onPaymentError?: (error: string) => void;
  onUseNewCard?: () => void;
  onCancel: () => void;
  savedCards?: SavedCard[];
  onCardsUpdate?: () => void;
  paymentType?: 'credit_charge' | 'printful_purchase';
  creditAmount?: number;
}

export const SavedCardsPayment: React.FC<SavedCardsPaymentProps> = ({
  userId,
  totalAmount,
  amount,  // 互換性のため
  onSelectCard,
  onAddNewCard,
  onPaymentSuccess,
  onPaymentError,
  onUseNewCard,
  onCancel,
  savedCards: propsSavedCards,
  onCardsUpdate,
  paymentType = 'printful_purchase',
  creditAmount
}) => {
  const displayAmount = totalAmount || amount || 0;
  const [savedCards, setSavedCards] = useState<SavedCard[]>(propsSavedCards || []);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!propsSavedCards);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (propsSavedCards) {
      setSavedCards(propsSavedCards);
      setLoading(false);
      // デフォルトカードを自動選択
      const defaultCard = propsSavedCards.find((card: SavedCard) => card.is_default);
      if (defaultCard) {
        setSelectedCardId(defaultCard.id);
      }
    } else {
      fetchSavedCards();
    }
  }, [userId, propsSavedCards]);

  const fetchSavedCards = async () => {
    try {
      const response = await axios.get(`${AISHA_API_BASE}/stripe/saved-cards/`, {
        params: { user_id: userId }
      });
      
      if (response.data.success) {
        setSavedCards(response.data.payment_methods);
        // デフォルトカードを自動選択
        const defaultCard = response.data.payment_methods.find((card: SavedCard) => card.is_default);
        if (defaultCard) {
          setSelectedCardId(defaultCard.id);
        }
      }
    } catch (error) {
      console.error('保存済みカード取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!confirm('このカードを削除しますか？')) return;
    
    try {
      const response = await axios.delete(`${AISHA_API_BASE}/stripe/saved-cards/delete/`, {
        params: { payment_method_id: cardId }
      });
      
      if (response.data.success) {
        setSavedCards(savedCards.filter(card => card.id !== cardId));
        if (selectedCardId === cardId) {
          setSelectedCardId(null);
        }
        // 親コンポーネントにカードリスト更新を通知
        if (onCardsUpdate) {
          onCardsUpdate();
        }
      }
    } catch (error) {
      console.error('カード削除エラー:', error);
    }
  };

  const handlePayWithCard = async () => {
    if (!selectedCardId) return;
    
    setProcessing(true);
    
    try {
      // クレジットチャージの場合
      if (paymentType === 'credit_charge' && creditAmount) {
        const response = await axios.post(`${AISHA_API_BASE}/stripe/payment-with-saved-card/`, {
          user_id: userId,
          payment_method_id: selectedCardId,
          amount: displayAmount,
          credit_amount: creditAmount
        });
        
        if (response.data.success) {
          if (onPaymentSuccess) {
            onPaymentSuccess(response.data.credit_balance);
          }
        } else {
          if (onPaymentError) {
            onPaymentError(response.data.error || '決済に失敗しました');
          }
        }
      } else {
        // Printful購入の場合（従来の処理）
        // 親側の決済処理(Stripe API・confirm-payment)が完了するまで await して
        // ボタン処理中状態を維持し、二重押下を防ぐ
        if (onSelectCard) {
          await onSelectCard(selectedCardId);
        }
      }
    } catch (error: any) {
      console.error('決済エラー:', error);
      if (onPaymentError) {
        onPaymentError(error.response?.data?.error || '決済処理中にエラーが発生しました');
      }
    } finally {
      setProcessing(false);
    }
  };

  const getCardBrandIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      case 'jcb':
        return '💳';
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
        <CreditCardIcon className="w-5 h-5 mr-2" />
        支払い方法を選択
      </h3>

      {savedCards.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400 mb-4">保存済みのカードがありません</p>
          <button
            onClick={onUseNewCard || onAddNewCard}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center mx-auto"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            新しいカードを追加
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {savedCards.map((card) => (
              <div
                key={card.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedCardId === card.id
                    ? 'border-indigo-500 bg-indigo-900/30'
                    : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                }`}
                onClick={() => setSelectedCardId(card.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      checked={selectedCardId === card.id}
                      onChange={() => setSelectedCardId(card.id)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getCardBrandIcon(card.brand)}</span>
                        <span className="text-white font-medium">
                          {card.brand} ****{card.last4}
                        </span>
                        {card.is_default && (
                          <span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded">
                            デフォルト
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">
                        有効期限: {card.exp_month}/{card.exp_year}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCard(card.id);
                    }}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onUseNewCard || onAddNewCard}
            className="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-all flex items-center justify-center"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            新しいカードを追加
          </button>

          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between text-white font-semibold">
              <span>お支払い金額</span>
              <span className="text-lg">¥{displayAmount.toLocaleString()}</span>
            </div>
            {creditAmount && (
              <div className="text-sm text-gray-400 mt-1">
                {creditAmount} クレジットを購入
              </div>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
            >
              キャンセル
            </button>
            <button
              onClick={handlePayWithCard}
              disabled={!selectedCardId || processing}
              className={`flex-1 px-4 py-3 rounded font-medium transition-colors ${
                selectedCardId && !processing
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {processing ? '処理中...' : 'このカードで支払う'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};